import React from 'react';
// Leave React here for JSX support in send/receive information

/*
    Note about channel capabilities

    If a capability has send/receive, you can pass information that will appear as a popover using `sendInformation` and `receiveInformation`.
    If a capability has just a generic support level, you can make it an object and pass `level` for the support level and `information`
    for the popover content.

    {
        send: SUPPORT_LEVEL.FULL,
        receive: SUPPORT_LEVEL.FULL,
        sendInformation: 'Some info in text',
        receiveInformation: <div>Some info in JSX</div>
    }

    {
        level: SUPPORT_LEVEL.FULL,
        information: 'Some info text'
    }

    The `capabilities` property of the channelData object is also used to populate the cards and modals visible in the channel pages. Here's a full description of the additional entry:

    {
        subtitle: 'high level description of the capability',
        description: 'additional information for the capability displayed on the modal',
        APILink: 'URL to the API documentation',
        docsLink: 'URL to the docs documentation',
        screenshot: require('path to screenshot image of the capability'),
        screenshotVisibleArea: 'top' or 'bottom'
    }
*/
export const SUPPORT_LEVEL = {
    FULL: 'full',
    PARTIAL: 'partial',
    PARTIAL_HOSTED: 'partial_hosted',
    NONE: 'none',
    NA: 'na',
};

export const CAPABILITIES = {
    text: {
        name: 'Text',
        link: '/guide/v1/sending-messages/#sending-text-messages-with-the-api',
        send: true,
        receive: true,
    },
    image: {
        name: 'Image',
        link: '/guide/v1/structured-messages/#images-stickers-and-gifs',
        send: true,
        receive: true,
    },
    file: {
        name: 'File',
        link: 'https://docs.smooch.io/rest/v1/#file',
        isExternalLink: true,
        send: true,
        receive: true,
    },
    audio: {
        name: 'Audio',
        link: 'https://docs.smooch.io/rest/v1/#file',
        isExternalLink: true,
        send: true,
        receive: true,
    },
    video: {
        name: 'Video',
        link: 'https://docs.smooch.io/rest/v1/#file',
        isExternalLink: true,
        send: true,
        receive: true,
    },
    gif: {
        name: 'GIF',
        link: '/guide/v1/structured-messages/#images-stickers-and-gifs',
        send: true,
        receive: true,
    },
    emoji: {
        name: 'Emoji',
        send: true,
        receive: true,
    },
    link: {
        name: 'Link',
        link: '/guide/v1/structured-messages/#link-buttons',
        send: true,
        receive: false,
    },
    webview: {
        name: 'Extension',
        link: '/guide/v1/conversation-extensions/',
        send: true,
        receive: false,
    },
    buy: {
        name: 'Buy',
        link: '/guide/v1/structured-messages/#buy-buttons',
        send: true,
        receive: false,
    },
    postback: {
        name: 'Postback',
        link: '/guide/v1/structured-messages/#postback-buttons',
        send: true,
        receive: false,
    },
    replies: {
        name: 'Reply',
        link: '/guide/v1/structured-messages/#reply-buttons',
        send: true,
        receive: false,
    },
    compoundMessages: {
        name: 'Compound Message',
        link: '/guide/v1/structured-messages/#compound-message',
        send: true,
        receive: false,
    },
    carousel: {
        name: 'Carousel',
        link: '/guide/v1/structured-messages/#carousel-messages',
        send: true,
        receive: false,
    },
    form: {
        name: 'Form',
        link: '/guide/v1/structured-messages/#forms',
        send: true,
        receive: false,
    },
    list: {
        name: 'List',
        link: '/guide/v1/structured-messages/#list-messages',
        send: true,
        receive: false,
    },
    typingIndicator: {
        name: 'Typing',
        link: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
        send: true,
        receive: true,
    },
    readIndicator: {
        name: 'Read',
        send: true,
        receive: true,
    },
    conversationStart: {
        name: 'Conversation Start',
        send: false,
        receive: true,
    },
    webMessengerLinking: {
        name: 'via Web Messenger',
        link: '/guide/v1/web-messenger#alternate-channels',
        send: false,
        receive: false,
    },
    location: {
        name: 'Location',
        send: true,
        receive: true,
    },
    locationRequest: {
        name: 'Location Request',
        link: '/guide/v1/structured-messages/#location-request-buttons',
        send: true,
    },
    channelDeliveryEvents: {
        name: 'Delivered to channel',
        link: '/guide/v1/delivery-events',
        receive: true,
    },
    userDeliveryEvents: {
        name: 'Delivered to user',
        link: '/guide/v1/delivery-events',
        receive: true,
    },
    channelMessageIds: {
        name: 'External messages are included in the delivery event',
        receive: true,
    },
    pushCapable: {
        name: 'Push capable',
        link: '/guide/v1/sending-messages/#automatic-message-delivery',
    },
    notificationEvents: {
        name: 'Notification',
        link: '/guide/v1/outbound-messaging',
        send: true,
    },
};

export const channelDataV1 = {
    ios: {
        icon: require('../images/channel-icons/ios.svg'),
        headerIcon: '/images/channel-header-logos/logo_sdk_ios_white.png',
        brandColor: '#656A7F',
        name: 'iOS SDK',
        isSDK: true,
        versionSourceUrl: 'https://api.github.com/repos/smooch/smooch-ios/releases',
        description:
            'The Sunshine Conversations iOS SDK is a highly customizable chat embeddable that can be added to any iOS app. It supports the Sunshine Conversations API <a href="/guide/v1/native-ios-sdk/#capabilities">capabilities</a>, <a href="/guide/v1/native-ios-sdk/#configuring-push-notifications">push notifications</a> and is fully <a href="/guide/v1/native-ios-sdk/#localization">localized</a>.',
        headerImg: '/images/ios_sdk.jpg',
        headerLinks: {
            GitHub: 'https://github.com/smooch/smooch-ios',
            Releases: 'https://github.com/smooch/smooch-ios/releases',
            FAQ: 'https://docs.smooch.io/faq/ios/',
            'SDK API': 'http://docs.smooch.io/api/ios/',
        },
        guideUrl: '/guide/v1/native-ios-sdk/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
                description: 'The iOS SDK supports a maximum text length of 4096 characters',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation: 'The iOS SDK displays files sent via the API and allows users to download them',
                receiveInformation: 'The iOS SDK allows users to upload and send any file from mobile device.',
                description:
                    'The iOS SDK displays files sent via the API and allows users to download them. Users can also send any file from their mobile device by choosing "Upload document" from the menu',
            },
            gif: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
                sendInformation:
                    'GIFs sent via the API as image messages will be displayed as a static image in the iOS SDK.',
                receiveInformation:
                    'GIFs sent as images by the user will also be displayed as a static image in the conversation.',
                description:
                    'GIFs sent via the API as image messages will be displayed as a static image in the iOS SDK. Gifs sent as images by the user will also be displayed as a static image in the conversation.',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'The iOS SDK displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/extension.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle:
                    'Send custom interactive experiences built with JavaScript, HTML & CSS and show them on top of the conversation',
                description:
                    'The iOS SDK displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/ios/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description:
                    'Enabled by our <a href="/guide/v1/stripe-payments/">Stripe integration</a>, securely receive one-time payments',
            },
            postback: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/reply.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'When including replies with a message, the iOS SDK  will display them at the bottom of the conversation. Users can quickly select one of them to send that reply.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/compound.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
            },
            carousel: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle:
                    'Send an horizontally scrollable set of cards that can contain text, image, and action buttons',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/ios/carousel.jpg'),
                screenshotVisibleArea: 'top',
                sendInformation: 'List messages are displayed as a Carousel on iOS',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                docsLink: '/guide/v1/structured-messages/#list-messages',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/typing.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#typing-activity',
                docsLink: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
                subtitle: 'Display a typing indicator',
                description:
                    'The iOS SDK can show that the agent is typing with a typing animation. The API allows to specify an agent name and avatar. A <a href="https://docs.smooch.io/rest/v1/#typing-events">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/read.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#business-read-receipt',
                subtitle: 'Display read status',
                description:
                    'The iOS SDK can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> is also available to signal when a user reads a conversation.',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#conversation-events',
                subtitle:
                    'Receive a <code>conversation:start</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user starts the conversation',
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/location.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive geolocation messages',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                receiveInformation:
                    'Upon sending a location, users will see a map of that location. Tapping on the map opens the Maps app centered on that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API.',
                description:
                    'Upon sending a location, users will see a map of that location. Tapping on the map opens the Maps app centered on that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/ios/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description:
                    "Once a user taps the request button, the iOS SDK will first ask for location permission and then send the user's location",
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NONE,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.NA,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    android: {
        icon: require('../images/channel-icons/android.svg'),
        headerIcon: '/images/channel-header-logos/logo_sdk_android_white.png',
        brandColor: '#656A7F',
        name: 'Android SDK',
        isSDK: true,
        versionSourceUrl: 'https://api.github.com/repos/smooch/smooch-android/releases',
        description:
            'The Sunshine Conversations android SDK is a highly customizable chat embeddable that can be added to any Android app. It supports all the Sunshine Conversations API <a href="/guide/v1/native-android-sdk/#capabilities">capabilities</a>, <a href="/guide/v1/native-android-sdk/#configuring-push-notifications">push notifications</a> and is fully <a href="/guide/v1/native-android-sdk/#localization">localized</a>.',
        headerImg: require('../images/capabilities-view/android/location.jpg'),
        headerLinks: {
            GitHub: 'https://github.com/smooch/smooch-android',
            Releases: 'https://github.com/smooch/smooch-android/releases',
            FAQ: 'https://docs.smooch.io/faq/android/',
            'SDK API': 'http://docs.smooch.io/api/android/',
        },
        guideUrl: '/guide/v1/native-android-sdk/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
                description: 'The Android SDK supports a maximum text length of 4096 characters',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
                description:
                    'The conversation interface supports the display of various image types. Tapping on an image will open it in an image viewer.',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation: 'The Android SDK displays files sent via the API and allows users to download them',
                receiveInformation: 'The Android SDK allows users to upload and send any file from mobile device.',
                description:
                    'The Android SDK displays files sent via the API and allows users to download them. Users can also send any file from their mobile device by choosing "Upload document" from the menu',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/android/gif-android.gif'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
                sendInformation: 'GIFs sent via the API will be animated',
                description:
                    'GIFs sent via the API will be animated on Android devices. Tapping on a Gif will open it in an image viewer. Note that for animated gifs, the image viewer will display the first frame only. The Android SDK does not include a way for users to choose GIFs to send.',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'The Android SDK displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/extensions.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle:
                    'Send custom interactive experiences built with JavaScript, HTML & CSS and show them on top of the conversation',
                description:
                    'The Android SDK displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/android/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postbacks</a> can trigger any logic on your server, which can then update the conversation back using the API.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                screenshot: require('../images/capabilities-view/android/reply.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'When including replies with a message, the Android SDK will display them at the bottom of the conversation. Users can quickly select one of them to send that reply.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/compound.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound message allows to send text, image and multiple buttons all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle:
                    'Send an horizontally scrollable set of cards that can contain text, image, and action buttons',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are displayed as a Carousel on Android',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/typing.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#typing-activity',
                docsLink: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
                subtitle: 'Display a typing indicator',
                description:
                    'The Android SDK can show that the agent is typing with an typing animation. The API allows to specify an agent name and avatar. A <a href="https://docs.smooch.io/rest/v1/#typing-events">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/read.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#business-read-receipt',
                subtitle: 'Display read status',
                description:
                    'The Android SDK can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> is also available to signal when a user has read the conversation.',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://github.com/smooch/smooch-web#startconversation',
                subtitle:
                    'Receive a <code>conversation:start</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user starts the conversation',
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/location.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive geolocation messages',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                receiveInformation:
                    'Upon sending a location, users will see a map of that location. Tapping on the map opens the Maps app centered on that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API. Location messages are rendered as text messages when a Maps API Key is not present.',
                description:
                    'Upon sending a location, users will see a map of that location. Tapping on the map opens the Maps app centered on that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API. Location messages are rendered as text messages when a Maps API Key is not present.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/android/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description:
                    "Once a user taps the request button, Android Native SDK will first ask for location permission and then send the user's location",
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NONE,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.NA,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    web: {
        icon: require('../images/channel-icons/web.svg'),
        headerIcon: '/images/channel-header-logos/logo_sdk_web_white.png',
        brandColor: '#656A7F',
        name: 'Web Messenger',
        isSDK: true,
        versionSourceUrl: 'https://api.github.com/repos/smooch/smooch-web/releases',
        description:
            'The Sunshine Conversations Web Messenger is a highly customizable messaging widget that can be added to any web page. Web Messenger is unique in that it allows the user seamlessly <a href="/guide/v1/web-messenger/#moving-to-other-channels">move the conversation</a> beyond the browser to a persistent channel like SMS, Facebook Messenger, or any other <a href="/guide/v1/intro-to-channels/">OTT channels.</a>',
        headerImg: '/images/carousel_web_horizontal.png',
        headerLinks: {
            GitHub: 'https://github.com/smooch/smooch-web',
            Releases: 'https://github.com/smooch/smooch-web/releases',
            FAQ: 'https://docs.smooch.io/faq/web-messenger/',
            'SDK API': 'https://github.com/smooch/smooch-web#api',
        },
        guideUrl: '/guide/v1/web-messenger/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/text.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/image.png'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/file.png'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation: 'Web Messenger displays files sent via the API and allows users to download them',
                receiveInformation:
                    'Web Messenger allows users to upload and send any file from their computer or mobile device.',
                description:
                    'The Web Messenger displays files sent via the API and allows users to download them. Users can also send any file from their computer or mobile device by choosing "Upload document" from the menu',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/gif.gif'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
                sendInformation: 'GIFs sent via the API will be animated in Web Messenger.',
                receiveInformation: 'GIFs sent via the Web Messenger will be animated.',
                description:
                    'GIFs sent via both the API and the Web Messenger will be animated. The Web messenger does not include a GIF picker.',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/emoji.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'Web Messenger displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them. Web Messenger does not include a visual emoji selector at the moment.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/link.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/webview.png'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle:
                    'Send custom interactive experiences built with JavaScript, HTML & CSS and show them on top of the conversation',
                description:
                    'Web Messenger displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details. </br> Extensions can be set to open automatically. See <a href="https://docs.smooch.io/rest/v1/#webview">our API documentation</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/web/buy.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description:
                    'Enabled by our <a href="/guide/v1/stripe-payments/">Stripe integration</a>, securely receive one-time payments',
            },
            postback: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/postback.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/replies.png'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'When including replies with a message, Web Messenger will display them at the bottom of the conversation. Users can quickly select one of them to send that reply.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/compound.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound messages have the capability to send text, image and multiple links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/carousel.png'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle:
                    'Send an horizontally scrollable set of cards that can contain text, image, and action buttons',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/form.png'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#form',
                docsLink: '/guide/v1/structured-messages/#forms',
                subtitle: 'Send a form that can contain text and email inputs as well as select dropdowns',
                description:
                    'Forms support up to a maximum of 20 fields. Each field must include a type, name and label. Optionally, <code>minSize</code>, <code>maxSize</code> and <code>placeholder</code> can be specified depending on the field type. For more information, see our <a href="https://docs.smooch.io/rest/v1/#form">API documentation</a>.',
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are displayed as a Carousel on Web',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                docsLink: '/guide/v1/structured-messages/#list-messages',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/typing.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#typing-activity',
                docsLink: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
                subtitle: 'Display a typing indicator',
                description:
                    'Web Messenger can show that the agent is typing with an typing animation. The API allows to specify an agent name and avatar. A <a href="https://docs.smooch.io/rest/v1/#typing-events">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/read.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#business-read-receipt',
                subtitle: 'Display read status',
                description:
                    'Web Messenger can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> is also available to signal when a user reads a conversation.',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://github.com/smooch/smooch-web#startconversation',
                subtitle:
                    'Receive a <code>conversation:start</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user starts the conversation',
            },
            webMessengerLinking: SUPPORT_LEVEL.NA,
            location: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.PARTIAL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/web/location.png'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                receiveInformation:
                    'Users will see the message rendered as text with a Google Maps link of the location. Location messages include longitude and latitude coordinates in the API.',
                description:
                    'Users will see the message rendered as text with a Google Maps link of the location. Location messages include longitude and latitude coordinates in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/web/locationrequest.png'),
                screenshotVisibleArea: 'bottom',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description:
                    "Once a user taps the request button, Web Messenger will first ask for location permission and then send the user's location",
            },
            userDeliveryEvents: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.NA,
            },
            pushCapable: SUPPORT_LEVEL.NONE,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    twilio: {
        icon: require('../images/channel-icons/twilio.svg'),
        headerIcon: '/images/channel-header-logos/logo_twilio_white.png',
        brandColor: '#F12139',
        name: 'Twilio',
        description:
            'Twilio SMS enables you to send and receive text messages over the carrier network to any phone, anywhere in the world. With phone numbers available in over 30 countries and competitive rates, Twilio’s inventory lets you pick the right numbers with the right capabilities for your project. Twilio handles ever-changing telecom logic and carrier specific rules to ensure your message reaches its destination.',
        headerImg: require('../images/capabilities-view/twilio/compound.jpg'),
        headerLinks: {
            'Twilio API Docs': 'https://www.twilio.com/docs/sms/api',
            'Integration API': 'https://docs.smooch.io/rest/v1/#twilio-integration',
        },
        guideUrl: '/guide/v1/twilio-sms/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twilio/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twilio/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twilio/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation: 'Users on SMS can receive files as hyperlinks to downloadable content.',
                receiveInformation: 'Twilio SMS delivers file content sent by users.',
                description:
                    'Twilio SMS allows users on SMS to receive files as hyperlinks to downloadable content and delivers file content sent by users.',
            },
            gif: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twilio/gif-twillio.gif'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twilio/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'Twilio SMS supports any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twilio/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twilio/extension-safari.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle: 'Send custom interactive experiences built with JavaScript, HTML & CSS.',
                description:
                    'Conversation extensions are delivered via hyperlink to SMS clients. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/twilio/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twilio/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send suggested text to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postbacks</a> can trigger any logic on your server, which can then update the conversation back using the API. Postback buttons are rendered as text on SMS.',
            },
            replies: {
                send: SUPPORT_LEVEL.PARTIAL,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                screenshot: require('../images/capabilities-view/twilio/reply.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Provide suggested responses.',
                description: 'Replies are rendered as text suggestion on SMS.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twilio/compound.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound messages have the capability to send text, image and multiple links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twilio/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle: 'Send a set of compound messages that contain images, text, and links',
                description:
                    'Carousels support up to a maximum of 10 compound message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally a description can be included as well. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are translated to text on Twilio',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.NA,
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.PARTIAL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/twilio/location.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Users will see the message rendered as text with a Google Maps link of the location. Some devices automatically unfurl links.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twilio/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Send a text for a location request to the user',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.FULL,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/outbound-messaging',
                subtitle: 'Send a notification to a user',
                APILink: 'https://docs.smooch.io/rest/v1/#notification',
            },
        },
    },
    messagebird: {
        icon: require('../images/channel-icons/messagebird.svg'),
        headerIcon: '/images/channel-header-logos/logo_messagebird_white.png',
        brandColor: '#237DCF',
        name: 'MessageBird',
        description:
            '<a href="https://www.messagebird.com/en-us/sms-gateway">MessageBird</a> is an SMS marketing, notification and communication service that allows users to send and receive personalized text messages.',
        headerImg: require('../images/capabilities-view/messagebird/image.jpg'),
        headerLinks: {
            'MessageBird API Docs': 'https://developers.messagebird.com/docs/sms-messaging',
            'Integration API': 'https://docs.smooch.io/rest/v1/#messagebird-integration',
        },
        guideUrl: '/guide/v1/messagebird-sms/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messagebird/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messagebird/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messagebird/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                description:
                    'MessageBird SMS displays files sent via the API and allows users to download them. Users can also send any file from their device.',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messagebird/gif-messagebird.gif'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messagebird/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'MessageBird SMS displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messagebird/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/messagebird/extension.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle: 'Send custom interactive experiences built with JavaScript, HTML & CSS.',
                description:
                    'In MessageBird SMS, the user can open a custom experience in their browser via a link. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/messagebird/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/messagebird/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send suggested text to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postbacks button</a> can trigger any logic on your server, which can then update the conversation back using the API.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                screenshot: require('../images/capabilities-view/messagebird/reply.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description: 'Users are prompted to reply to the message with suggestions.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/messagebird/compound.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound messages have the capability to send text, image and multiple links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/messagebird/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle: 'Send a set of messages that contain text and links',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally a description can be included as well. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are translated to text on MessageBird',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.NA,
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.PARTIAL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/messagebird/location.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Users will see the message rendered as text with a Google Maps link of the location. Some devices automatically unfurl links.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/messagebird/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Send a text for a location request to the user',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.FULL,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/outbound-messaging',
                subtitle: 'Send a notification to a user',
                APILink: 'https://docs.smooch.io/rest/v1/#notification',
            },
        },
    },
    telegram: {
        icon: require('../images/channel-icons/telegram.svg'),
        headerIcon: '/images/channel-header-logos/logo_telegram_white.png',
        brandColor: '#0088cc',
        name: 'Telegram',
        description:
            'With over 200 million active monthly users, Telegram is a free messaging app with a focus on speed and security. Thanks to its distributed infrastructure and encryption, Telegram is fast and secure. It can be used on multiple devices at the same time — messages are synced seamlessly across any number of phones, tablets or computers. Telegram allows businesses to send messages, photos, files of any type, as well as use other rich capabilities like quick replies, postbacks, compound messages and carousels.',
        headerImg: require('../images/capabilities-view/telegram/buy.jpg'),
        headerLinks: {
            'Telegram API Docs': 'https://core.telegram.org/bots',
            'Integration API': 'https://docs.smooch.io/rest/v1/#telegram-integration',
        },
        guideUrl: '/guide/v1/telegram/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/text.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/image.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/telegram/file.jpg'),
                screenshotVisibleArea: 'bottom',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation: 'Telegram displays files sent via the API and allows users to download them',
                receiveInformation:
                    'Telegram allows users to upload and send any file from their computer or mobile device.',
                description:
                    'Telegram displays files sent via the API and allows users to download them. Users can also send any file from their device.',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshotVisibleArea: 'bottom',
                screenshot: require('../images/capabilities-view/telegram/gif.jpg'),
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/emoji.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'Telegram displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/link.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/telegram/extension.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle: 'Send custom interactive experiences built with JavaScript, HTML & CSS.',
                description:
                    'In Telegram, the user can open a custom experience in their browser via a link. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/telegram/buy.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/postback.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to the user trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                screenshot: require('../images/capabilities-view/telegram/reply.jpg'),
                screenshotVisibleArea: 'bottom',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'Users are prompted to reply to the message with suggested responses. Telegram will display a selection of responses below the input field.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/compound.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound messages have the capability to send text, an image and links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle: 'Send a set of cards that can contain text, image, and action buttons',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are translated to a series of separate messages on Telegram',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/telegram/typing.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#typing-activity',
                docsLink: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
                subtitle: 'Display a typing indicator',
                description:
                    'Telegram can display to the user that the agent is typing. A <a href="https://docs.smooch.io/rest/v1/#typing-events">webhook event</a> is also available to signal when the user is typing.',
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/text.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#business-read-receipt',
                subtitle: 'Display read status',
                description:
                    'Receive a <code>conversation:read</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user reads the conversation',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/start.jpg'),
                APILink: 'https://github.com/smooch/smooch-web#startconversation',
                screenshotVisibleArea: 'bottom',
                subtitle:
                    'Receive a <code>conversation:start</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user starts the conversation',
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/telegram/location.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Upon sending a location, users will see a map of that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/telegram/location-request.jpg'),
                screenshotVisibleArea: 'bottom',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Send a location request to the user',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NA,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.FULL,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    messenger: {
        icon: require('../images/channel-icons/messenger.svg'),
        headerIcon: '/images/channel-header-logos/logo_messenger_white.png',
        brandColor: '#4d87d8',
        name: 'Facebook Messenger',
        description:
            'Facebook Messenger has over 1.2 million users and is among the richest messaging channels available for businesses to reach customers. Engage them with experiences that includes carousels, quick replies, emojis and conversation extensions. Messenger supports it all.',
        headerImg: require('../images/capabilities-view/messenger/compound.jpg'),
        headerLinks: {
            'Official Messenger Docs': 'https://developers.facebook.com/docs/messenger-platform',
            'Integration API': 'https://docs.smooch.io/rest/v1/#facebook-messenger-integration',
        },
        guideUrl: '/guide/v1/facebook-messenger/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/messenger/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation: 'Messenger displays files sent via the API and allows users to download them.',
                description: 'Messenger displays files sent via the API and allows users to download them.',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/gif.gif'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
                sendInformation: 'GIFs sent via the API will be animated in messenger',
                description: 'GIFs sent via the API will be animated in messenger.',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'Messenger displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/extensions.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle:
                    'Send custom interactive experiences built with JavaScript, HTML & CSS and show them on top of the conversation',
                description:
                    'Messenger displays conversation extensions on top of the conversation, in three sizes: compact, tall, and full. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
            },
            postback: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/reply.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'When including replies with a message, messenger will display them at the bottom of the conversation. Users can quickly select one of them to send that reply.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/compound.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound messages have the capability to send text, image and multiple links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle:
                    'Send an horizontally scrollable set of cards that can contain text, image, and action buttons',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one supported action. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.NONE,
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/messenger/typing.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#typing-activity',
                docsLink: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
                subtitle: 'Display a typing indicator',
                description: 'Messenger can display to your users when an agent is typing with a typing animation.',
            },
            readIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/read.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#business-read-receipt',
                subtitle: 'Display read status',
                description:
                    'Messenger can show that the agent has read the conversation by displaying "Seen" on the last message. A <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> is also available to signal when a user reads a conversation.',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/messenger/conversation-start.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#conversation-events',
                subtitle:
                    'Receive a <code>conversation:start</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user starts the conversation',
                description: 'Subscribe to webhook events when the user starts a conversation',
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/messenger/location.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Users will see the message rendered as text with a Google Maps link of the location. Location messages include longitude and latitude coordinates in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.NONE,
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NONE,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.FULL,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    line: {
        icon: require('../images/channel-icons/line.svg'),
        headerIcon: '/images/channel-header-logos/logo_line_white.png',
        brandColor: '#13B91A',
        name: 'LINE',
        description:
            'LINE is a Japan based rapidly growing mobile messaging app that offers free voice and video calls, messages, and a limitless variety of stickers. With over 100 million users worldwide, LINE’s expanding platform is a great way to stay in touch with your customers.',
        headerImg: require('../images/capabilities-view/line/compound.jpg'),
        headerLinks: {
            'Official LINE Docs': 'https://developers.line.me/en/docs/messaging-api/overview/',
            'Integration API': 'https://docs.smooch.io/rest/v1/#line-integration',
        },
        guideUrl: '/guide/v1/line/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/line/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/line/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/line/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                description: 'LINE displays files sent via the API as a link and allows users to download them.',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NA,
                screenshotVisibleArea: 'top',
                screenshot: require('../images/capabilities-view/line/gif.jpg'),
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/line/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'LINE displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/line/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/line/extension-safari.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle: 'Send custom interactive experiences built with JavaScript, HTML & CSS.',
                description:
                    'On LINE, the user can open a custom experience in their browser via a hyperlink. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/line/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/line/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                screenshot: require('../images/capabilities-view/line/reply.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'Users are prompted to reply to the message with suggested responses. Line will display a selection of responses below as part of the message bubble.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/line/compound.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description: 'Compound messages are made up of text, an image and links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/line/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle:
                    'Send an horizontally scrollable set of cards that can contain text, image, and action buttons',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are displayed as a Carousel on LINE',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://github.com/smooch/smooch-web#startconversation',
                subtitle:
                    'Receive a <code>conversation:start</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user starts the conversation',
            },
            webMessengerLinking: {
                level: SUPPORT_LEVEL.PARTIAL,
                information:
                    'Users can scan a code to start following your LINE account, but the conversation is not linked with the one on Web Messenger.',
            },
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/line/location.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Upon sending a location, users will see a map of that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/line/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Send a location request to the user',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NA,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.NA,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    wechat: {
        icon: require('../images/channel-icons/wechat.svg'),
        headerIcon: '/images/channel-header-logos/logo_wechat_white.png',
        brandColor: '#2BBB01',
        name: 'WeChat',
        description:
            'WeChat is a Chinese multi-purpose messaging, social media and mobile payment app. With over 1 billion monthly active users, it’s the most popular chat app for Chinese-speaking people. WeChat’s social commerce platform enables users to transact with businesses for things like booking a taxi, a flight, or a doctor’s appointment. It’s like Facebook, Skype, Slack, Amazon, PayPal, and Apple Wallet all rolled up into one.',
        headerImg: require('../images/capabilities-view/wechat/gif.jpg'),
        headerLinks: {
            'Official WeChat Docs': 'https://developers.weixin.qq.com/doc/offiaccount/en/Getting_Started/Overview.html',
            'Integration API': 'https://app.smooch.io/integrations/wechat',
        },
        guideUrl: '/guide/v1/wechat/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/wechat/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/wechat/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/wechat/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                description: 'WeChat displays files sent via the API as a hyperlink.',
            },
            gif: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                screenshotVisibleArea: 'top',
                screenshot: require('../images/capabilities-view/wechat/gif.jpg'),
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
                description: 'WeChat displays GIFs sent via the API as still images.',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/wechat/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'WeChat displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/wechat/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/wechat/extension-safari.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle: 'Send custom interactive experiences built with JavaScript, HTML & CSS.',
                description:
                    'On WeChat conversation extensions are displayed in a browser. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/wechat/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/wechat/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send text to the user to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postbacks</a> can trigger any logic on your server, which can then update the conversation back using the API. Postback buttons are rendered as text suggestions on WeChat.',
            },
            replies: {
                send: SUPPORT_LEVEL.PARTIAL,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                screenshot: require('../images/capabilities-view/wechat/reply.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description: 'Users are prompted to reply to the message with suggestions.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/wechat/compound.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound messages have the capability to send text, image and multiple links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/wechat/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle: 'Send a set of message items that can contain text and action links',
                description:
                    'Carousel messages are displayed as plain text in WeChat because of the lack of support for rich cards and compound messages.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/wechat/typing.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#typing-activity',
                description:
                    'WeChat can show that the agent is typing with a typing animation. Note that this only works within 30 seconds of sending or receiving a message.',
                docsLink: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
                subtitle: 'Display a typing indicator',
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/wechat/location.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Users will see the message rendered as text with a Google Maps link of the location. Location messages include longitude and latitude coordinates in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/wechat/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Send a location request to the user',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NA,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.NA,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    viber: {
        icon: require('../images/channel-icons/viber.svg'),
        headerIcon: '/images/channel-header-logos/logo_viber_white.png',
        brandColor: '#574E92',
        name: 'Viber',
        description:
            'With over 260 million monthly active users, Viber is a calling and messaging app. With a free and secure connection, users worldwide can communicate through high-quality audio and video calls, messaging, and much more. All Viber calls and chats are protected by built-in end-to-end encryption, so conversations are always secure.',
        headerImg: require('../images/capabilities-view/viber/carousel.jpg'),
        headerLinks: {
            'Official Viber Docs': 'https://developers.viber.com/docs/',
            'Integration API': 'https://docs.smooch.io/rest/v1/#viber-integration',
        },
        guideUrl: '/guide/v1/viber/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/viber/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                description: 'Viber displays files sent via the API as a hyperlink and allows users to download them.',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NONE,
                screenshotVisibleArea: 'top',
                screenshot: require('../images/capabilities-view/viber/viber-gif.jpg'),
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'Viber displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/viber/extension.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle: 'Send custom interactive experiences built with JavaScript, HTML & CSS.',
                description:
                    'In Viber, the user can open a custom experience in their browser via a hyperlink. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/viber/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/viber/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send text to the user trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postbacks</a> are sent to Viber as predefined text that when sent by the user as a message notify the server by webhook. The server can then act on the event and post messages back to the user in response.',
            },
            replies: {
                send: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                screenshot: require('../images/capabilities-view/viber/reply.jpg'),
                screenshotVisibleArea: 'bottom',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description:
                    'Users are prompted to reply to the message with suggested responses. Telegram will display a selection of responses below the input field.',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/compound.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description: 'Compound messages combine text, an image and multiple links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle: 'Send a set of message items that can contain text and action links',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are displayed as carousels on Viber',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#business-read-receipt',
                subtitle: 'Display read status',
                description:
                    'Receive a <code>conversation:read</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user reads the conversation',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/viber/getting-started-screen.jpg'),
                APILink: 'https://github.com/smooch/smooch-web#startconversation',
                screenshotVisibleArea: 'bottom',
                subtitle:
                    'Receive a <code>conversation:start</code> <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> when the user starts the conversation',
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/viber/location.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Upon sending a location, users will see a map of that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/viber/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Send a location request to the user',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NONE,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.FULL,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    twitter: {
        icon: require('../images/channel-icons/twitter.svg'),
        headerIcon: '/images/channel-header-logos/logo_twitter_white.png',
        brandColor: '#1DA1F3',
        name: 'Twitter DM',
        description:
            'Like most social media platforms, Twitter gives you an option to send private messages to users. Twitter Direct Messages enable businesses to take an interaction with a user from the public timeline to a private conversation.',
        headerImg: require('../images/capabilities-view/twitter/compound.jpg'),
        headerLinks: {
            'Official Twitter Docs': 'https://developer.twitter.com/en/docs/direct-messages/api-features',
            'Integration API': 'https://docs.smooch.io/rest/v1/#twitter-dm-integration',
        },
        guideUrl: '/guide/v1/twitter/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twitter/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twitter/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                screenshot: require('../images/capabilities-view/twitter/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                description:
                    'Twitter displays files sent via the API as a hyperlink and allows users to download them.',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshotVisibleArea: 'top',
                screenshot: require('../images/capabilities-view/twitter/twitter-gif.gif'),
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Send animated GIFs',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twitter/emoji.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Unicode Emojis✨',
                description:
                    'Twitter displays any unicode emoji sent in text messages. Mobile users can use the emoji keyboard on their device to send them.',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twitter/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links',
                description: 'Transform links into clear calls to action',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twitter/twitter-extension.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle: 'Send custom interactive experiences built with JavaScript, HTML & CSS.',
                description:
                    'On Twitter, the user can open a custom experience in their browser via a hyperlink. See <a href="/guide/v1/conversation-extensions/">our guide</a> for more details.',
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/twitter/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description: 'Request payments within a conversation',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postback buttons</a> notify the server by webhook when clicked. The server can then act on the click and post messages back to the user in response to the click.',
            },
            replies: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/twitter/compound.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with multiple actions',
                description:
                    'Compound messages provide the capability to send text, an image and links all in a single message.',
            },
            carousel: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twitter/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle: 'Send a set of cards that can contain text, image, and action buttons',
                description:
                    'Carousels support up to a maximum of 10 message items. Each message item must include a title and at least one <a href="/guide/v1/web-messenger/#supported-actions">supported action</a>. Optionally, an image and a description can be included as well. Images can be displayed in two different aspect ratios, “square” and “horizontal”. For more information, see our <a href="https://docs.smooch.io/rest/v1/#carousel">API documentation</a>.',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are displayed as text on Twitter',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.FULL,
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.FULL,
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.NA,
            },
            webMessengerLinking: SUPPORT_LEVEL.PARTIAL,
            location: {
                screenshot: require('../images/capabilities-view/twitter/location.jpg'),
                screenshotVisibleArea: 'top',
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NA,
                subtitle: 'Send and receive geolocation messages',
                description:
                    'Users will see the message rendered as text with a Google Maps link of the location. Location messages include longitude and latitude coordinates in the API.',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/twitter/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Send a location request to the user',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.NA,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.NA,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
            },
        },
    },
    apple: {
        icon: require('../images/channel-icons/apple.svg'),
        headerIcon: '/images/channel-header-logos/logo_apple_white.png',
        brandColor: '#00000',
        name: 'Apple Messages for Business',
        description:
            'Apple Messages for Business is a channel built to reach customers through Messages on any Apple device. The channel prides itself on its rich set of messaging features including bot and AI capabilities, security, and ability to connect to the wide world of iOS apps and add-ons.',
        headerLinks: {
            'Official Apple docs':
                'https://register.apple.com/resources/messages/messaging-documentation/#introduction',
        },
        guideUrl: '/guide/v1/apple-messages-for-business/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
            },
            audio: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            file: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Send and receive any file types',
            },
            gif: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Send animated GIFs',
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Unicode Emojis✨',
            },
            link: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
            },
            webview: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
            },
            replies: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with text, an image and multiple actions',
            },
            carousel: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
            },
            form: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                docsLink: '/guide/v1/structured-messages/#list-messages',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.NONE,
                APILink: 'https://docs.smooch.io/rest/v1/#typing-activity',
                docsLink: '/guide/v1/sending-messages/#sending-typing-activity-with-the-api',
                subtitle: 'Display a typing indicator',
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.NONE,
            },
            webMessengerLinking: SUPPORT_LEVEL.NONE,
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                subtitle: 'Send and receive geolocation messages',
            },
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.NONE,
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                subtitle: 'Request the current location of the user',
            },
            userDeliveryEvents: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            channelDeliveryEvents: {
                send: SUPPORT_LEVEL.NONE,
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            override: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
        },
    },
    whatsapp: {
        icon: require('../images/channel-icons/whatsapp.svg'),
        headerIcon: '/images/channel-header-logos/logo_whatsapp.png',
        brandColor: '#25D366',
        name: 'WhatsApp',
        description:
            'More than 1.5 billion global monthly users in over 180 countries use WhatsApp to stay in touch with friends and family, anytime and anywhere. WhatsApp offers simple, secure, reliable messaging and calling, available on phones all over the world. Now open to enterprises in early access, WhatsApp is poised to be the world’s most powerful business messaging channel.',
        headerImg: '/images/whatsapp.jpg',
        headerLinks: {
            'Official WhatsApp docs': 'https://developers.facebook.com/docs/whatsapp',
        },
        guideUrl: '/guide/v1/whatsapp/',
        capabilities: {
            text: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/whatsapp/text.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#text',
                docsLink: '/guide/v1/sending-messages/',
                subtitle: 'Plain text messages.',
                description:
                    'WhatsApp has a maximum length of 4096 characters for the text sent. Messages generated by AI will include the generative AI disclaimer, which counts towards the character limit.',
            },
            image: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/whatsapp/image.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#image',
                docsLink: '/guide/v1/structured-messages/#images-stickers-and-gifs',
                subtitle: 'Display static images',
                description:
                    'WhatsApp has a maximum length of 4096 characters for the text sent. Messages generated by AI will include the generative AI disclaimer, which counts towards the character limit.',
            },
            file: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/whatsapp/file.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive any file types',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation: 'WhatsApp displays files sent via the API and allows users to download them',
                receiveInformation:
                    'WhatsApp allows users to upload and send any file from their computer or mobile device.',
                description:
                    'WhatsApp displays files sent via the API and allows users to download them. Users can also send any file from their computer or mobile device by choosing "Document" from the "📎" menu.<br />Whatsapp has a maximum length of 4096 characters for the text sent. Messages generated by AI will include the generative AI disclaimer, which counts towards the character limit.',
            },
            audio: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/whatsapp/audio.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Send and receive rich audio message',
                APILink: 'https://docs.smooch.io/rest/v1/#file',
                docsLink: '/guide/v1/sending-messages/',
                sendInformation:
                    'WhatsApp displays audio files sent via the API and allows users to listen to them directly in the app.',
                receiveInformation:
                    'WhatsApp allows users to upload and send certain audio files from their computer or mobile device.',
                description:
                    'WhatsApp displays audio files sent via the API and allows users to listen to them directly in the app. Users can also send any file from their computer or mobile device by choosing "Audio" from the "📎" menu',
            },
            video: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            emoji: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                screenshot: require('../images/capabilities-view/whatsapp/emoji.jpg'),
                screenshotVisibleArea: 'top',
                subtitle: 'Unicode Emojis✨',
                description:
                    'WhatsApp displays any unicode emoji sent in text messages. Users can use the emoji keyboard on their device to send them.',
            },
            gif: {
                send: SUPPORT_LEVEL.PARTIAL,
                receive: SUPPORT_LEVEL.FULL,
                screenshotVisibleArea: 'top',
                screenshot: require('../images/capabilities-view/whatsapp/gif.jpg'),
                subtitle: 'Send animated GIFs',
                sendInformation: 'GIFs sent via the API are translated to a link in WhatsApp',
                receiveInformation:
                    'Users can send GIFs by choosing "Photo & Video Library" then GIF search from the "+" menu. GIFs are delivered as MP4 files',
                description:
                    'GIFs sent via the API are translated to a link in WhatsApp. GIFs sent by users are delivered as MP4 files',
            },
            location: {
                send: SUPPORT_LEVEL.FULL,
                receive: SUPPORT_LEVEL.FULL,
                subtitle: 'Send and receive geolocation messages',
                screenshot: require('../images/capabilities-view/whatsapp/location.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#location',
                description:
                    'Upon sending a location, users will see a map of that location. Location messages include longitude and latitude coordinates along with a Google Maps link (as text) in the API.',
            },
            link: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/whatsapp/link.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#link',
                docsLink: '/guide/v1/structured-messages/#link-buttons',
                subtitle: 'Display web links as buttons',
                description: 'Link actions are rendered as inline links on WhatsApp',
            },
            webview: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'Extensions opens in external browser',
                screenshot: require('../images/capabilities-view/whatsapp/extensions.jpg'),
                screenshotVisibleArea: 'bottom',
                APILink: 'https://docs.smooch.io/rest/v1/#webview',
                docsLink: '/guide/v1/conversation-extensions/',
                subtitle:
                    'Send custom interactive experiences built with JavaScript, HTML & CSS and show them on top of the conversation',
                description: "WhatsApp opens conversation extensions in the user's browser",
            },
            buy: {
                send: SUPPORT_LEVEL.NONE,
                screenshot: require('../images/capabilities-view/whatsapp/buy.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#buy',
                docsLink: '/guide/v1/stripe-payments/',
                subtitle: 'Request payments within a conversation',
                description:
                    'Enabled by our <a href="/guide/v1/stripe-payments/">Stripe integration</a>, securely receive one-time payments',
            },
            postback: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'Postback are rendered as text',
                screenshot: require('../images/capabilities-view/whatsapp/postback.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#postback',
                docsLink: '/guide/v1/structured-messages/#postback-buttons',
                subtitle: 'Send buttons to trigger events on your server',
                description:
                    '<a href="/guide/v1/structured-messages/#postback-buttons">Postback buttons</a> can trigger any logic on your server, which can then update the conversation back using the API. Postback buttons are rendered as text on WhatsApp',
            },
            replies: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'Replies are rendered as text',
                screenshot: require('../images/capabilities-view/whatsapp/reply.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#reply',
                docsLink: '/guide/v1/structured-messages/#reply-buttons',
                subtitle: 'Suggest a few answers to reply to a message',
                description: 'Replies are rendered as text suggestion on WhatsApp',
            },
            compoundMessages: {
                send: SUPPORT_LEVEL.PARTIAL,
                screenshot: require('../images/capabilities-view/whatsapp/compound.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                subtitle: 'Compose messages with text, an image and multiple actions',
                description: 'Compound messages are split into multiple messages on WhatsApp',
            },
            carousel: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'Carousels are rendered as text',
                screenshot: require('../images/capabilities-view/whatsapp/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#carousel',
                docsLink: '/guide/v1/structured-messages/#carousels-and-lists',
                subtitle:
                    'Send a horizontally scrollable set of cards that can contain text, image, and action buttons',
                description: 'Carousels are rendered as text on WhatsApp',
            },
            form: {
                send: SUPPORT_LEVEL.NONE,
            },
            list: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'List messages are rendered as text',
                screenshot: require('../images/capabilities-view/whatsapp/carousel.jpg'),
                screenshotVisibleArea: 'top',
                APILink: 'https://docs.smooch.io/rest/v1/#post-message',
                docsLink: '/guide/v1/structured-messages/#list-messages',
                description: 'List are rendered as text on WhatsApp',
            },
            typingIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.NA,
            },
            readIndicator: {
                send: SUPPORT_LEVEL.NA,
                receive: SUPPORT_LEVEL.FULL,
                APILink: 'https://docs.smooch.io/rest/v1/#business-read-receipt',
                subtitle: 'Display read status',
                description:
                    'Receive events when the user has read your messages. A <a href="https://docs.smooch.io/rest/v1/#conversation-events">webhook event</a> is also available to signal when a user reads a conversation. WhatsApp can\'t show that the agent has read the conversation.',
            },
            conversationStart: {
                receive: SUPPORT_LEVEL.NA,
            },
            webMessengerLinking: SUPPORT_LEVEL.FULL,
            locationRequest: {
                send: SUPPORT_LEVEL.PARTIAL,
                sendInformation: 'Location requests are rendered as text',
                screenshot: require('../images/capabilities-view/whatsapp/location-request.jpg'),
                screenshotVisibleArea: 'top',
                docsLink: '/guide/v1/structured-messages/#location-request-buttons',
                subtitle: 'Request the current location of the user',
                APILink: 'https://docs.smooch.io/rest/v1/#location-request',
                description: 'Location request are translated to text on WhatsApp',
            },
            userDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the user',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelDeliveryEvents: {
                receive: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/delivery-events',
                subtitle: 'Confirm the delivery of a message to the channel',
                APILink: 'https://docs.smooch.io/rest/v1/#delivery-events',
            },
            channelMessageIds: {
                receive: SUPPORT_LEVEL.FULL,
            },
            pushCapable: SUPPORT_LEVEL.FULL,
            notificationEvents: {
                send: SUPPORT_LEVEL.FULL,
                docsLink: '/guide/v1/outbound-messaging',
                subtitle: 'Send a notification to a user',
                APILink: 'https://docs.smooch.io/rest/v1/#notification',
            },
        },
    },
};

export const CHANNELS = [
    'ios',
    'android',
    'web',
    'twilio',
    'messagebird',
    'telegram',
    'messenger',
    'twitter',
    'line',
    'wechat',
    'viber',
    'whatsapp',
    'apple',
].map((id) =>
    Object.assign(
        {
            id,
        },
        channelDataV1[id]
    )
);



// WEBPACK FOOTER //
// ./src/data/channelCapabilitiesV1.js